import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import DeleteConfirmModal from '../../common/DeleteConfirmModal';
import LucideSymbolIcon from '../../icons/LucideSymbolIcon';
import { useRegisterPortalRefresh } from '../../../contexts/PortalRefreshContext';
import {
  deleteAdminPartenaireSponsor,
  fetchAdminPartenairesSponsors,
  patchAdminPartenaireSponsor,
} from '../../../services/academiaAdminService';
import { resolveBrandingLogoUrl } from '../../../utils/brandingUrl';
import AdminPageShell from '../AdminPageShell';
import AdminToast from '../AdminToast';
import { AcademicPanelCard } from './AcademicPanelCard';

type Row = {
  id: string;
  nom: string;
  urlLogo: string;
  urlSite?: string | null;
  categorie: 'PARTENAIRE' | 'SPONSOR';
  ordre: number;
  estActif: boolean;
  montantChiffreAffaires?: number | null;
  dateDebutContrat?: string | null;
  dateFinContrat?: string | null;
  dureeContratMois?: number | null;
  urlContrat?: string | null;
  detailsContrat?: string | null;
  dateCreation?: string | null;
  dateMiseAJour?: string | null;
};

function categorieLabel(value: Row['categorie']) {
  return value === 'SPONSOR' ? 'Sponsor' : 'Partenaire';
}

function formatDate(value?: string | null) {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('fr-FR');
}

function DetailGrid({ rows }: { rows: Array<{ label: string; value: React.ReactNode }> }) {
  return (
    <dl className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
      {rows.map((row) => (
        <div key={row.label} className="rounded-xl border border-outline-variant/15 bg-surface-container-low/40 p-4">
          <dt className="text-xs font-bold uppercase tracking-wide text-on-surface-variant">{row.label}</dt>
          <dd className="mt-2 text-sm font-medium text-slate-900">{row.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export default function AcademiaPartenaireSponsorDetailPanel() {
  const navigate = useNavigate();
  const { partenaireId = '' } = useParams<{ partenaireId: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [row, setRow] = useState<Row | null>(null);

  const load = useCallback(async () => {
    if (!partenaireId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchAdminPartenairesSponsors();
      const found = (Array.isArray(data) ? data : []).find((item) => item.id === partenaireId) ?? null;
      setRow(found);
      if (!found) setError('Partenaire ou sponsor introuvable.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossible de charger le détail.');
      setRow(null);
    } finally {
      setLoading(false);
    }
  }, [partenaireId]);

  useEffect(() => {
    void load();
  }, [load]);

  useRegisterPortalRefresh(() => void load());

  const contractStatus = useMemo(() => {
    if (!row) return '—';
    const now = Date.now();
    const debutOk = row.dateDebutContrat ? new Date(row.dateDebutContrat).getTime() <= now : true;
    const finOk = row.dateFinContrat ? new Date(row.dateFinContrat).getTime() >= now : true;
    return debutOk && finOk ? 'Valide' : 'Expiré';
  }, [row]);

  const toggleActif = async () => {
    if (!row) return;
    setSaving(true);
    setError(null);
    try {
      await patchAdminPartenaireSponsor(row.id, { estActif: !row.estActif });
      setToast(row.estActif ? 'Élément masqué sur le site public.' : 'Élément visible sur le site public.');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Mise à jour impossible.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!row) return;
    setSaving(true);
    setError(null);
    try {
      await deleteAdminPartenaireSponsor(row.id);
      setDeleteOpen(false);
      navigate('/admin/partenaires-sponsors', { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Suppression impossible.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminPageShell
      title={row?.nom ?? 'Détail partenaire / sponsor'}
      description="Fiche détaillée du partenaire ou sponsor."
      isLoading={loading}
      isError={Boolean(error) && !row}
      errorMessage={error ?? undefined}
      onRetry={() => void load()}
      sideCard={{
        label: 'Statut',
        value: row?.estActif ? 'Actif' : 'Inactif',
        helper: row ? `${categorieLabel(row.categorie)} · contrat ${contractStatus.toLowerCase()}` : '—',
        sideLinks: [{ label: 'Retour à la liste', to: '/admin/partenaires-sponsors', icon: 'arrow_back' }],
      }}
      primaryAction={
        <Link to="/admin/partenaires-sponsors" className="inline-flex items-center gap-2 rounded-xl bg-white px-4 py-2.5 text-sm font-bold text-primary shadow-md hover:bg-white/95">
          <LucideSymbolIcon name="arrow_back" tone="primary" size={18} />
          Retour à la liste
        </Link>
      }
    >
      <AdminToast message={toast} variant="success" onClose={() => setToast(null)} />
      <DeleteConfirmModal
        open={deleteOpen}
        title="Supprimer ce partenaire ou sponsor ?"
        description={row ? <>Vous allez supprimer <strong>{row.nom}</strong>.</> : undefined}
        isConfirming={saving}
        onCancel={() => {
          if (!saving) setDeleteOpen(false);
        }}
        onConfirm={() => void handleDelete()}
      />

      {error && row ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>
      ) : null}

      {row ? (
        <div className="space-y-6">
          <AcademicPanelCard
            icon="handshake"
            title="Résumé"
            subtitle="Informations générales, commerciales et contractuelles."
            badge={
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => void toggleActif()}
                  className={`rounded-full px-3 py-1 text-xs font-bold ${
                    row.estActif ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-700'
                  }`}
                >
                  {row.estActif ? 'Actif' : 'Inactif'}
                </button>
              </div>
            }
          >
            <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start">
              <div className="flex h-24 w-36 shrink-0 items-center justify-center rounded-2xl border border-outline-variant/15 bg-white p-3">
                {resolveBrandingLogoUrl(row.urlLogo) ? (
                  <img src={resolveBrandingLogoUrl(row.urlLogo)} alt={row.nom} className="max-h-16 max-w-full object-contain" />
                ) : (
                  <LucideSymbolIcon name="image" tone="inherit" size={22} className="text-slate-400" />
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => void toggleActif()}
                  className="rounded-xl border border-outline-variant/25 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  {row.estActif ? 'Masquer du site public' : 'Rendre visible'}
                </button>
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => setDeleteOpen(true)}
                  className="rounded-xl border border-red-200 px-4 py-2 text-sm font-semibold text-red-700 hover:bg-red-50"
                >
                  Supprimer
                </button>
              </div>
            </div>

            <DetailGrid
              rows={[
                { label: 'Nom', value: row.nom },
                { label: 'Catégorie', value: categorieLabel(row.categorie) },
                { label: 'Ordre', value: row.ordre },
                {
                  label: 'Chiffre d’affaires',
                  value:
                    row.montantChiffreAffaires != null
                      ? `${Number(row.montantChiffreAffaires).toLocaleString('fr-FR')} CDF`
                      : '—',
                },
                { label: 'Date début contrat', value: formatDate(row.dateDebutContrat) },
                { label: 'Date fin contrat', value: formatDate(row.dateFinContrat) },
                { label: 'Durée contrat', value: row.dureeContratMois != null ? `${row.dureeContratMois} mois` : '—' },
                { label: 'Statut contrat', value: contractStatus },
                {
                  label: 'Site web',
                  value: row.urlSite ? (
                    <a href={row.urlSite} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                      Ouvrir le site
                    </a>
                  ) : (
                    '—'
                  ),
                },
                {
                  label: 'Contrat',
                  value: row.urlContrat ? (
                    <a href={row.urlContrat} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                      Ouvrir le document
                    </a>
                  ) : (
                    '—'
                  ),
                },
                { label: 'Créé le', value: formatDate(row.dateCreation) },
                { label: 'Mis à jour le', value: formatDate(row.dateMiseAJour) },
              ]}
            />
          </AcademicPanelCard>

          <AcademicPanelCard icon="description" title="Détails du contrat">
            <p className="text-sm leading-relaxed text-slate-800">{row.detailsContrat?.trim() || 'Aucun détail saisi.'}</p>
          </AcademicPanelCard>
        </div>
      ) : null}
    </AdminPageShell>
  );
}
